import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from 'date-fns';

interface TimeLog {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  status: string;
}

interface DayData {
  date: Date;
  totalHours: number;
  isComplete: boolean;
  hasLogs: boolean;
  logs: TimeLog[];
}

interface TimeLogCalendarProps {
  logs: TimeLog[];
}

const TimeLogCalendar: React.FC<TimeLogCalendarProps> = ({ logs }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);

  // Generate calendar days
  const generateCalendarDays = (): DayData[] => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const days: DayData[] = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      const currentDay = day;

      // Find logs for this day
      const dayLogs = logs.filter(log => {
        const logDate = parseISO(log.clock_in_time);
        return isSameDay(logDate, currentDay);
      });

      // Calculate total hours and completion status
      let totalHours = 0;
      let isComplete = dayLogs.length > 0;

      dayLogs.forEach(log => {
        if (log.clock_out_time) {
          const start = new Date(log.clock_in_time).getTime();
          const end = new Date(log.clock_out_time).getTime();
          totalHours += (end - start) / (1000 * 60 * 60);
        } else {
          isComplete = false; // Has an incomplete session
        }
      });

      days.push({
        date: currentDay,
        totalHours,
        isComplete: isComplete && dayLogs.length > 0,
        hasLogs: dayLogs.length > 0,
        logs: dayLogs
      });

      day = addDays(day, 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const previousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDay(null);
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDay(null);
  };

  const getDayClassName = (dayData: DayData) => {
    const baseClasses = "min-h-20 p-2 border border-gray-200 transition-colors cursor-pointer";
    const notCurrentMonth = !isSameMonth(dayData.date, currentMonth);

    if (notCurrentMonth) {
      return `${baseClasses} bg-gray-50 text-gray-400`;
    }

    if (!dayData.hasLogs) {
      return `${baseClasses} bg-white hover:bg-gray-50`;
    }

    if (dayData.isComplete) {
      return `${baseClasses} bg-green-50 hover:bg-green-100 border-green-200`;
    }

    // Has incomplete logs
    return `${baseClasses} bg-yellow-50 hover:bg-yellow-100 border-yellow-200`;
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return format(date, 'h:mm a');
  };

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={previousMonth}
          className="px-3 py-1 rounded-md hover:bg-gray-100 text-gray-600"
        >
          ← Previous
        </button>
        <h3 className="text-lg font-semibold text-[#3A5E63]">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button
          onClick={nextMonth}
          className="px-3 py-1 rounded-md hover:bg-gray-100 text-gray-600"
        >
          Next →
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
          <span>Complete</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-50 border border-yellow-200 rounded"></div>
          <span>Incomplete</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-white border border-gray-200 rounded"></div>
          <span>No Work</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0 border border-gray-300 rounded-lg overflow-hidden">
        {/* Day headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-[#3A5E63] text-white text-center py-2 text-sm font-semibold">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {calendarDays.map((dayData, index) => (
          <div
            key={index}
            className={getDayClassName(dayData)}
            onClick={() => dayData.hasLogs && setSelectedDay(dayData)}
          >
            <div className="text-sm font-medium text-gray-700">
              {format(dayData.date, 'd')}
            </div>
            {dayData.hasLogs && (
              <div className="mt-1">
                <div className="text-xs font-semibold text-[#3A5E63]">
                  {dayData.totalHours.toFixed(1)}h
                </div>
                {!dayData.isComplete && (
                  <div className="text-xs text-yellow-600 font-medium">
                    Active
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Day Details Modal */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-[#3A5E63]">
                  {format(selectedDay.date, 'MMMM d, yyyy')}
                </h3>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Total Hours</div>
                <div className="text-2xl font-bold text-[#3A5E63]">
                  {selectedDay.totalHours.toFixed(2)} hours
                </div>
                <div className={`text-sm mt-1 ${selectedDay.isComplete ? 'text-green-600' : 'text-yellow-600'}`}>
                  {selectedDay.isComplete ? '✓ All sessions complete' : '⚠ Has incomplete session'}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700">Time Log Details</h4>
                {selectedDay.logs.map((log) => {
                  const start = new Date(log.clock_in_time);
                  const end = log.clock_out_time ? new Date(log.clock_out_time) : null;
                  const duration = end
                    ? ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toFixed(2)
                    : 'In Progress';

                  return (
                    <div key={log.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="text-sm text-gray-600">Clock In</div>
                          <div className="font-medium text-gray-900">{formatTime(log.clock_in_time)}</div>
                        </div>
                        <div className="flex-1 text-right">
                          <div className="text-sm text-gray-600">Clock Out</div>
                          <div className="font-medium text-gray-900">
                            {log.clock_out_time ? formatTime(log.clock_out_time) : (
                              <span className="text-green-600 flex items-center justify-end gap-1">
                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-100">
                        <div className="text-sm text-gray-600">Duration</div>
                        <div className="font-semibold text-[#3A5E63]">
                          {typeof duration === 'string' ? duration : `${duration} hours`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => setSelectedDay(null)}
                className="mt-4 w-full py-2 px-4 bg-[#3A5E63] text-white rounded-lg hover:bg-[#2d4a4e]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeLogCalendar;
